import type { MethodologyUpdateDTO } from '@protector/entities/methodology/models';

type Component = {
  id: string;
  component_id: string;
  priority: number;
  class_name: string;
  aliases_ids?: string[];
};

type Phenomenon = {
  id: string;
  component_id: string;
  priority: number;
  mandatory_photo?: boolean;
  component_clusters?: unknown;
  ordered_components: Component[];
};

type InspectionGroup = {
  id: string;
  component_id?: string;
  name: unknown;
  priority: number;
  mandatory_photo?: boolean;
  ordered_extra_dimensions?: { extra_dimension_id: string; priority: number }[];
  ordered_categories: {
    id: string;
    component_id: string;
    priority: number;
    ordered_phenomenons: Phenomenon[];
  }[];
};

type MethodologyForUpdate = MethodologyUpdateDTO & {
  station_id?: string;
  default_methodology?: boolean;
  scouting_available_until?: string;
  season_field_ids?: string[];
  inspection_layout?: MethodologyUpdateDTO['inspection_layout'] & {
    independent_variables?: { id: string; component_id: string; priority: number }[];
    variables_validation?: unknown;
    ordered_inspection_groups: InspectionGroup[];
  };
};

const componentType = (className: string) => {
  if (className === 'OrderedCharacteristicDTO') return 'CHARACTERISTIC';
  if (className === 'OrderedIndicatorDTO') return 'INDICATOR';
  throw new Error(`Unsupported methodology component class: ${className}`);
};

export const mapMethodologyForUpdate = (methodology: MethodologyForUpdate) => {
  const layout = methodology.inspection_layout;
  const context = methodology.analytic_context_dto;

  return {
    name: methodology.name,
    description: methodology.description,
    crop_id: methodology.crop_id,
    station_id: methodology.station_id,
    parent_id: methodology.parent_id,
    product_set_id: methodology.product_set_id,
    constants: methodology.constants,
    default_methodology: methodology.default_methodology,
    scouting_available_until: methodology.scouting_available_until,
    season_ids: methodology.season_ids,
    season_property_ids: methodology.season_property_ids,
    season_field_ids: methodology.season_field_ids ?? methodology.season_area_ids,
    analytic_context: context && { id: context.id, custom_indicators: context.custom_indicator_dtos },
    inspection_layout: layout && {
      id: layout.id,
      characteristic_ids: layout.characteristic_ids,
      indicator_ids: layout.indicator_ids,
      independent_variables: layout.independent_variables?.map(({ id, component_id, priority }) => ({ id, component_id, priority })),
      variables_validation: layout.variables_validation,
      ordered_inspection_groups: layout.ordered_inspection_groups.map(group => ({
        id: group.id,
        component_id: group.component_id,
        name: group.name,
        priority: group.priority,
        mandatory_photo: group.mandatory_photo,
        ordered_extra_dimensions: group.ordered_extra_dimensions?.map(({ extra_dimension_id, priority }) => ({
          extra_dimension_id,
          priority
        })),
        ordered_categories: group.ordered_categories.map(category => ({
          id: category.id,
          component_id: category.component_id,
          priority: category.priority,
          ordered_phenomena: category.ordered_phenomenons.map(phenomenon => ({
            id: phenomenon.id,
            component_id: phenomenon.component_id,
            priority: phenomenon.priority,
            mandatory_photo: phenomenon.mandatory_photo,
            component_clusters: phenomenon.component_clusters,
            ordered_components: phenomenon.ordered_components.map(({ id, component_id, priority, class_name, aliases_ids }) => ({
              id,
              component_id,
              priority,
              ...(aliases_ids ? { aliases_ids } : {}),
              type: componentType(class_name)
            }))
          }))
        }))
      }))
    },
    display_layout: methodology.display_layout
  };
};
